import { memo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { K, getAdapterTypeLabel } from '../apiServiceI18n';
import {
  DEFAULT_PERF_FILTERS,
  TIMEFRAME_1D,
  TIMEFRAME_1M,
  TIMEFRAME_1W,
  TIMEFRAME_CUSTOM,
  formatDateTimeDisplay,
  isAllSelected,
} from './apiServicePerformanceUtils';

const tabBtnSx = (active) => ({
  minWidth: 44,
  px: 1.5,
  py: 0.5,
  fontWeight: 700,
  fontSize: '0.8rem',
  textTransform: 'none',
  borderRadius: 1,
  bgcolor: active ? '#3d4654' : '#fff',
  color: active ? '#fff' : '#5f6878',
  border: '1px solid #c5cad3',
  '&:hover': { bgcolor: active ? '#3d4654' : '#f3f4f6' },
});

function MultiSelectField({
  label,
  value,
  options,
  onChange,
  disabled,
  allLabel,
}) {
  const [open, setOpen] = useState(false);

  const renderValue = (selected) => {
    if (isAllSelected(selected)) return allLabel;
    if (selected.length === 1) {
      const opt = options.find((o) => o.value === selected[0]);
      return opt?.label || selected[0];
    }
    return `${selected.length} selected`;
  };

  const handleChange = (e) => {
    const next = e.target.value;
    const arr = typeof next === 'string' ? next.split(',') : next;
    if (arr.includes('ALL') && !value.includes('ALL')) {
      onChange(['ALL']);
    } else if (arr.length > 1 && arr.includes('ALL')) {
      onChange(arr.filter((v) => v !== 'ALL'));
    } else if (!arr.length) {
      onChange(['ALL']);
    } else {
      onChange(arr);
    }
    setOpen(false);
  };

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        MenuProps={{
          disablePortal: true,
          PaperProps: {
            sx: {
              maxHeight: 280,
              width: 300,
              maxWidth: '90vw',
            },
          },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        }}
      >
        <MenuItem value="ALL">{allLabel}</MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              py: 0.75,
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function ApiServicePerformanceToolbar({
  timeframe,
  onTimeframeChange,
  customRange,
  onCustomRangeChange,
  draftFilters,
  onDraftFiltersChange,
  filterOptions,
  hasActiveFilters,
  onApplyFilters,
  onClearFilters,
  apiFilterLocked,
}) {
  const { t } = useTranslation();
  const [dateAnchor, setDateAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [draftFromDate, setDraftFromDate] = useState(customRange.from);
  const [draftToDate, setDraftToDate] = useState(customRange.to);
  const [draftFromTime, setDraftFromTime] = useState(formatDateTimeDisplay(customRange.from).slice(11));
  const [draftToTime, setDraftToTime] = useState(formatDateTimeDisplay(customRange.to).slice(11));

  const openDate = Boolean(dateAnchor);
  const openFilter = Boolean(filterAnchor);

  const setFilterField = (key, value) => {
    onDraftFiltersChange({ ...draftFilters, [key]: value });
  };

  const clampQuantity = (raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_PERF_FILTERS.quantity;
    return Math.min(99, Math.max(10, Math.round(n)));
  };

  const applyDateRange = () => {
    const parseTime = (date, timeStr, fallbackHour, fallbackMin) => {
      const base = dayjs(date);
      const match = String(timeStr || '').match(/^(\d{1,2}):(\d{2})$/);
      if (match) return base.hour(Number(match[1])).minute(Number(match[2])).second(0);
      return base.hour(fallbackHour).minute(fallbackMin).second(0);
    };
    onCustomRangeChange({
      from: parseTime(draftFromDate, draftFromTime, 0, 0),
      to: parseTime(draftToDate, draftToTime, 23, 59),
    });
    onTimeframeChange(TIMEFRAME_CUSTOM);
    setDateAnchor(null);
  };

  const wfgOptions = filterOptions.workflowGroups.map((g) => ({ value: g, label: g }));
  const wfOptions = filterOptions.workflows.map((w) => ({ value: w, label: w }));
  const apiOptions = filterOptions.apis.map((a) => ({ value: a.id, label: a.label }));
  const adapterOptions = filterOptions.adapters.map((a) => ({
    value: a,
    label: getAdapterTypeLabel(a, t),
  }));

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
      <Stack direction="row" spacing={0.75}>
        {[TIMEFRAME_1M, TIMEFRAME_1W, TIMEFRAME_1D].map((tf) => (
          <Button key={tf} onClick={() => onTimeframeChange(tf)} sx={tabBtnSx(timeframe === tf)}>
            {tf}
          </Button>
        ))}
      </Stack>

      <Tooltip title={t(K.API_SVC_PERF_DATE_RANGE_TOOLTIP)}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalendarMonthIcon />}
          onClick={(e) => {
            setDraftFromDate(customRange.from);
            setDraftToDate(customRange.to);
            setDraftFromTime(formatDateTimeDisplay(customRange.from).slice(11));
            setDraftToTime(formatDateTimeDisplay(customRange.to).slice(11));
            setDateAnchor(e.currentTarget);
          }}
          sx={{ textTransform: 'none', borderColor: '#c5cad3', color: '#5f6878' }}
        >
          {formatDateTimeDisplay(customRange.from)} – {formatDateTimeDisplay(customRange.to).slice(11)}
        </Button>
      </Tooltip>

      <Box sx={{ flex: 1 }} />

      {hasActiveFilters && (
        <Button
          size="small"
          color="inherit"
          endIcon={<CloseIcon fontSize="small" />}
          onClick={onClearFilters}
          sx={{ textTransform: 'none', color: '#5f6878', fontWeight: 600 }}
        >
          {t(K.API_SVC_PERF_CLEAR_FILTERS)}
        </Button>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<FilterListIcon />}
        onClick={(e) => setFilterAnchor(e.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        {t(K.API_SVC_PERF_FILTER_BY)}
      </Button>

      <Popover
        open={openDate}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 560 }}>
          <Stack direction="row" spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={dayjs(draftFromDate)}
                onChange={(d) => setDraftFromDate(d)}
              />
              <DateCalendar value={dayjs(draftToDate)} onChange={(d) => setDraftToDate(d)} />
            </LocalizationProvider>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label={t(K.LBL_FROM)}
              value={formatDateTimeDisplay(draftFromDate).slice(0, 10) + ' ' + draftFromTime}
              onChange={(e) => {
                const parts = e.target.value.split(' ');
                if (parts[1]) setDraftFromTime(parts[1]);
              }}
              fullWidth
            />
            <TextField
              size="small"
              label={t(K.LBL_TO)}
              value={formatDateTimeDisplay(draftToDate).slice(0, 10) + ' ' + draftToTime}
              onChange={(e) => {
                const parts = e.target.value.split(' ');
                if (parts[1]) setDraftToTime(parts[1]);
              }}
              fullWidth
            />
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={applyDateRange} sx={{ bgcolor: '#3d4654' }}>
              {t(K.BTN_APPLY)}
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={openFilter}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            {t(K.API_SVC_PERF_FILTER_BY)}
          </Typography>
          <Stack spacing={2}>
            <MultiSelectField
              label={t(K.WORKFLOW_GROUP)}
              value={draftFilters.workflowGroups}
              options={wfgOptions}
              onChange={(v) => setFilterField('workflowGroups', v)}
              allLabel={t(K.ALL)}
            />
            <MultiSelectField
              label={t(K.WORKFLOW)}
              value={draftFilters.workflows}
              options={wfOptions}
              onChange={(v) => setFilterField('workflows', v)}
              disabled={apiFilterLocked}
              allLabel={t(K.ALL)}
            />
            <MultiSelectField
              label={t(K.LBL_NAME)}
              value={draftFilters.apis}
              options={apiOptions}
              onChange={(v) => setFilterField('apis', v)}
              allLabel={t(K.ALL)}
            />
            <MultiSelectField
              label={t(K.API_SVC_COL_ADAPTER)}
              value={draftFilters.adapters}
              options={adapterOptions}
              onChange={(v) => setFilterField('adapters', v)}
              disabled={apiFilterLocked}
              allLabel={t(K.ALL)}
            />
            <TextField
              size="small"
              type="number"
              label={t(K.API_SVC_PERF_QUANTITY)}
              value={draftFilters.quantity}
              disabled={apiFilterLocked}
              inputProps={{ min: 10, max: 99 }}
              onChange={(e) => setFilterField('quantity', e.target.value)}
              onBlur={(e) => setFilterField('quantity', clampQuantity(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setFilterField('quantity', clampQuantity(e.target.value));
              }}
            />
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                onApplyFilters({
                  ...draftFilters,
                  quantity: clampQuantity(draftFilters.quantity),
                });
                setFilterAnchor(null);
              }}
              sx={{ bgcolor: '#3d4654' }}
            >
              {t(K.BTN_APPLY)}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

export default memo(ApiServicePerformanceToolbar);
